import { FC, ReactNode } from 'react';

import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import { Table } from '../../../components/table/index';
import {
  DividendPayout,
  DividendSortBy,
  DividendSortOrder,
} from '../../../hooks/dividend/dividend.types';
import { useScreenSize } from '../../../hooks/use-screen-size.hook';
import { DividendPayoutCard } from './dividend-payout-card';
import { DividendSortingCell } from './dividend-sorting-cell';
import { DividendTableRow } from './dividend-table-row';
import {
  DividendColumn,
  dividendColumnOrderMap,
  dividendColumnTranslationKey,
} from './dividends-table.types';

type Props = {
  companyId: string;
  currencySymbol: string;
  onEdit: (payout: DividendPayout) => void;
  onSortClick: (field: DividendSortBy) => void;
  onView: (payout: DividendPayout) => void;
  payouts: DividendPayout[];
  sortField: DividendSortBy | null;
  sortOrder: DividendSortOrder | null;
};

export const DividendsTable: FC<Props> = ({
  companyId,
  currencySymbol,
  onEdit,
  onSortClick,
  onView,
  payouts,
  sortField,
  sortOrder,
}) => {
  const { width } = useScreenSize();

  if (width < 460) {
    return (
      <div className="flex flex-col gap-3 px-4 py-3">
        {payouts.map((payout) => (
          <DividendPayoutCard
            companyId={companyId}
            currencySymbol={currencySymbol}
            key={payout.id}
            payout={payout}
          />
        ))}
      </div>
    );
  }

  const columnsTitle: Record<DividendColumn, number | ReactNode | string> = {
    ...dividendColumnTranslationKey,
    [DividendColumn.PAYOUT_NAME]: (
      <DividendSortingCell
        onSortClick={onSortClick}
        sortByCell={DividendSortBy.NAME}
        sortField={sortField}
        sortOrder={sortOrder}
        title={<AppFormattedMessage id="PAYOUT_NAME" />}
      />
    ),
    [DividendColumn.APPROVAL_DATE]: (
      <DividendSortingCell
        onSortClick={onSortClick}
        sortByCell={DividendSortBy.APPROVAL_DATE}
        sortField={sortField}
        sortOrder={sortOrder}
        title={<AppFormattedMessage id="APPROVAL_DATE" />}
      />
    ),
    [DividendColumn.PAYOUT_DATE]: (
      <DividendSortingCell
        onSortClick={onSortClick}
        sortByCell={DividendSortBy.PAYOUT_DATE}
        sortField={sortField}
        sortOrder={sortOrder}
        title={<AppFormattedMessage id="PAYOUT_DATE" />}
      />
    ),
  };

  return (
    <Table
      className="min-w-[1060px] table-fixed"
      columnOrder={dividendColumnOrderMap}
      columnsTitle={columnsTitle}
      tHeadClassNames={{
        thClassName: {
          // Column widths taken from design 4972:13567 (1212px grid). The payout name is sized
          // too — leaving it unset let it absorb every spare pixel and render ~573px wide.
          general: 'h-11 px-4',
          [DividendColumn.PAYOUT_NAME]: 'w-[303px]',
          [DividendColumn.SHARE_CLASS]: 'w-[141px]',
          [DividendColumn.FREQUENCY]: 'w-[107px]',
          [DividendColumn.PERIOD]: 'w-[101px]',
          [DividendColumn.TOTAL_PAYOUT]: 'w-[104px]',
          [DividendColumn.APPROVAL_DATE]: 'w-[134px]',
          [DividendColumn.PAYOUT_DATE]: 'w-[122px]',
          [DividendColumn.STATUS]: 'w-[150px]',
          [DividendColumn.ACTIONS]: 'w-14',
        },
      }}
    >
      {payouts.map((payout) => (
        <DividendTableRow
          companyId={companyId}
          currencySymbol={currencySymbol}
          key={payout.id}
          onEdit={onEdit}
          onView={onView}
          payout={payout}
        />
      ))}
    </Table>
  );
};
